export type AssistantTelemetryEventName =
  | 'assistant_opened'
  | 'assistant_mode_switched'
  | 'assistant_handoff_clicked'
  | 'assistant_session_created'
  | 'assistant_session_selected'
  | 'assistant_session_deleted'
  | 'assistant_session_renamed'
  | 'assistant_message_submitted'

export interface AssistantTelemetryEvent {
  id: string
  name: AssistantTelemetryEventName
  timestamp: string
  path: string
  payload: Record<string, string | number | boolean | null>
}

const STORAGE_KEY = 'assistant-telemetry-events'
const MAX_EVENTS = 200
const EVENT_NAME = 'assistant:telemetry'

const canUseBrowserStorage = () => {
  return typeof window !== 'undefined' && typeof localStorage !== 'undefined'
}

const readEvents = (): AssistantTelemetryEvent[] => {
  if (!canUseBrowserStorage()) return []

  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? (parsed as AssistantTelemetryEvent[]) : []
  } catch {
    return []
  }
}

const writeEvents = (events: AssistantTelemetryEvent[]) => {
  if (!canUseBrowserStorage()) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(events.slice(-MAX_EVENTS)))
  } catch {
    // Ignore local storage write failures (quota/private mode).
  }
}

export const trackAssistantEvent = (
  name: AssistantTelemetryEventName,
  payload: Record<string, string | number | boolean | null> = {}
) => {
  if (typeof window === 'undefined') return

  const event: AssistantTelemetryEvent = {
    id: crypto.randomUUID(),
    name,
    timestamp: new Date().toISOString(),
    path: window.location.pathname + window.location.search,
    payload
  }

  const nextEvents = [...readEvents(), event]
  writeEvents(nextEvents)

  window.dispatchEvent(
    new CustomEvent<AssistantTelemetryEvent>(EVENT_NAME, {
      detail: event
    })
  )
}

export const getAssistantTelemetryEvents = () => readEvents()
